import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Empty,
  Popconfirm,
  Popover,
  Tag
} from 'antd';
import { debounce, filter, find, forEach, isEmpty, map, trim } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import FilterSelectedIconComponent from '../../../app/components/iconComponents/FilterSelectedComponent';
import ExportIcon from '../../../assets/export.svg';
import FilterIcon from '../../../assets/filter.svg';
import ImportIcon from '../../../assets/import.svg';
import {
  DYNAMIC_FIELDS_DATA,
  ROUTES,
  SKIP_RECORD,
  exportOptions
} from '../../../common/constants';
import { checkPermissions, handleExportCommon } from '../../../common/utils';
import AccessControl from '../../../components/AccessControl';
import LoaderComponent from '../../../components/LoaderComponent';
import Portal from '../../../components/Portal';
import SearchComponent from '../../../components/SearchComponent';
import TableComponent from '../../../components/TableComponent';
import history from '../../../historyData';
import ImportModal from '../../imports/components/ImportModal';
import {
  DELETE_DYNAMIC_FIELD,
  UPDATE_DYNAMIC_FIELD
} from '../graphql/Mutations';
import {
  DYNAMIC_FIELD_FILTER,
  GET_DYNAMIC_FIELDS,
  INDUSTRY_FILTER,
  LINE_OF_BUSINESS_FILTER
} from '../graphql/Queries';

let scrollDebounce = null;

const statusRender = (status) => {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'PUBLISH':
      return 'Published';
    case 'INACTIVE':
      return 'Inactive';
    default:
      return '-';
  }
};

const DynamicFieldsTable = ({ setSelectedDynamicKey }) => {
  const {
    state: { pageSize, permissions }
  } = useContext(AppContext);

  const initialPaginationValue = {
    total: 0,
    current: 1
  };

  const initialDynamicFieldsFilter = {
    skip: 0,
    limit: pageSize,
    sortOn: 'createdAt',
    sortBy: 'DESC'
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [showImportModal, setShowImportModal] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [dynamicFieldFilter, setDynamicFieldsFilter] = useState(
    initialDynamicFieldsFilter
  );
  const [filters, setFilters] = useState(null);
  const [filtersCopyState, setFiltersCopyState] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterList, setFilterList] = useState([]);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [searchValue, setSearchValue] = useState(null);

  const [
    deleteDynamicFields,
    { loading: deleteDynamicFieldsLoading }
  ] = useMutation(DELETE_DYNAMIC_FIELD, {
    onError() {}
  });

  const [
    updateDynamicField,
    { loading: updateDynamicFieldLoading }
  ] = useMutation(UPDATE_DYNAMIC_FIELD, {
    onError() {}
  });

  const [dynamicFields, { loading, data }] = useLazyQuery(GET_DYNAMIC_FIELDS, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const pagination = {
        ...paginationProp,
        defaultPageSize: pageSize,
        total: res?.dynamicFields?.count
      };
      setPaginationProp(pagination);
    },
    onError() {}
  });

  const [industryFilters] = useLazyQuery(INDUSTRY_FILTER, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (scrollFlag) {
        const optionsCopy = [...filterList];
        forEach(res?.industryFilters?.data, (item) =>
          optionsCopy?.push(item?.label)
        );
        setFilterList(optionsCopy);
        setScrollFlag(false);
      } else {
        const optionsCopy = [];
        forEach(res?.industryFilters?.data, (item) =>
          optionsCopy?.push(item?.label)
        );
        setFilterList(optionsCopy);
      }
      setFilterIsEnd(res?.industryFilters?.data?.length < SKIP_RECORD);
      setFilterLoading(false);
    },
    onError: () => {
      setFilterLoading(false);
    }
  });

  const [lineOfBusinessFilters] = useLazyQuery(LINE_OF_BUSINESS_FILTER, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (scrollFlag) {
        const optionsCopy = [...filterList];
        forEach(res?.lineOfBusinessFilters?.data, (item) =>
          optionsCopy?.push(item?.label)
        );
        setFilterList(optionsCopy);
        setScrollFlag(false);
      } else {
        const optionsCopy = [];
        forEach(res?.lineOfBusinessFilters?.data, (item) =>
          optionsCopy?.push(item?.label)
        );
        setFilterList(optionsCopy);
      }
      setFilterIsEnd(res?.lineOfBusinessFilters?.data?.length < SKIP_RECORD);
      setFilterLoading(false);
    },
    onError: () => {
      setFilterLoading(false);
    }
  });

  const [dynamicFieldFilters] = useLazyQuery(DYNAMIC_FIELD_FILTER, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (scrollFlag) {
        const optionsCopy = [...filterList];
        forEach(res?.dynamicFieldFilters?.data, (item) => {
          optionsCopy?.push(item?.[filterIndex]);
        });
        setFilterList(optionsCopy);
        setScrollFlag(false);
      } else {
        const optionsCopy = [];
        forEach(res?.dynamicFieldFilters?.data, (item) => {
          optionsCopy?.push(item?.[filterIndex]);
        });
        setFilterList(optionsCopy);
      }
      setFilterIsEnd(res?.dynamicFieldFilters?.data?.length < SKIP_RECORD);
      setFilterLoading(false);
    },
    onError: () => {
      setFilterLoading(false);
    }
  });

  useEffect(() => {
    // eslint-disable-next-line no-undef
    window?.addEventListener('beforeunload', () => {
      // eslint-disable-next-line no-undef
      if (window?.location?.pathname === ROUTES?.DYNAMIC_FIELDS)
        // eslint-disable-next-line no-undef
        window?.history?.pushState({}, '', window?.location?.pathname);
    });
    return () => {
      // eslint-disable-next-line no-undef
      window?.removeEventListener('beforeunload', () => {
        // eslint-disable-next-line no-undef
        window?.history?.pushState({}, '', window?.location?.pathname);
      });
    };
  }, []);

  useEffect(() => {
    const permitted = checkPermissions(permissions, ['FET_DYNAMIC_FIELD_LIST']);
    const whereFilter = history?.location?.state;
    let parsedObject;

    // dynamicFieldFilter, paginationProp, filters
    if (!isEmpty(whereFilter)) {
      parsedObject = JSON?.parse(whereFilter?.stringifyObject);
    }

    if (permitted) {
      dynamicFields({
        variables: {
          filter: parsedObject?.dynamicFieldFilter ?? dynamicFieldFilter,
          where: {
            ...(parsedObject?.filters && { ...parsedObject?.filters }),
            entityType: 'PRODUCT'
          }
        }
      });

      if (!isEmpty(parsedObject)) {
        setSearchValue(parsedObject?.dynamicFieldFilter?.search);
        setFilters(parsedObject?.filters);
        setFiltersCopyState(parsedObject?.filters);
        const sorter = {
          order:
            parsedObject?.dynamicFieldFilter?.sortBy === 'ASC'
              ? 'ascend'
              : 'descend',
          columnKey: parsedObject?.dynamicFieldFilter?.sortOn
        };
        setSortedInfo({ ...sorter });
        setPaginationProp({ ...parsedObject?.paginationProp });
        setDynamicFieldsFilter({ ...parsedObject?.dynamicFieldFilter });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFilterData = (confirm) => {
    dynamicFields({
      variables: {
        filter: { ...dynamicFieldFilter, skip: 0 },
        where: {
          ...(filtersCopyState && { ...filtersCopyState }),
          entityType: 'PRODUCT'
        }
      }
    });
    setFilters(filtersCopyState);
    setDynamicFieldsFilter({
      ...dynamicFieldFilter,
      skip: 0
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      switch (filterIndex) {
        case 'industry':
          industryFilters({
            variables: {
              filter: {
                sortOn: 'label',
                sortBy: 'ASC',
                skip: 0,
                limit: 20,
                search: filterSearch,
                getDBField: 'label'
              }
            }
          });
          break;

        case 'lineOfBusiness':
          lineOfBusinessFilters({
            variables: {
              filter: {
                sortOn: 'label',
                sortBy: 'ASC',
                skip: 0,
                limit: 20,
                search: filterSearch,
                justShow: true,
                getDBField: 'label'
              }
            }
          });
          break;

        case 'fieldType':
          setFilterList(DYNAMIC_FIELDS_DATA);
          setFilterLoading(false);
          break;

        default:
          dynamicFieldFilters({
            variables: {
              filter: {
                sortOn: 'status',
                sortBy: 'ASC',
                skip: 0,
                limit: 20,
                search: filterSearch,
                distinct: true,
                getDBField: 'status'
              }
            }
          });
          break;
      }
    }
    if (!filterVisible) {
      setFilterSearch('');
      setFilterIsEnd(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVisible]);

  const handleShowImportModal = (visible) => {
    setShowImportModal(visible);
  };

  const onScroll = (event, dataIndex) => {
    const { target } = event;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};

    if (scrollDebounce) {
      scrollDebounce?.cancel();
      scrollDebounce = null;
    }
    scrollDebounce = debounce(() => {
      const scrolledToBottom = scrollTop + offsetHeight >= scrollHeight - 5;
      if (
        scrolledToBottom &&
        filterIndex === dataIndex &&
        filterVisible &&
        !filterIsEnd
      ) {
        setScrollFlag(true);

        switch (dataIndex) {
          case 'industry':
            industryFilters({
              variables: {
                filter: {
                  sortOn: 'label',
                  sortBy: 'ASC',
                  skip: filterList?.length,
                  limit: 20,
                  search: filterSearch,
                  getDBField: 'label'
                }
              }
            });
            break;

          case 'lineOfBusiness':
            lineOfBusinessFilters({
              variables: {
                filter: {
                  sortOn: 'label',
                  sortBy: 'ASC',
                  skip: filterList?.length,
                  limit: 20,
                  search: filterSearch,
                  justShow: true,
                  getDBField: 'label'
                }
              }
            });
            break;
          default:
            dynamicFieldFilters({
              variables: {
                filter: {
                  sortOn: 'status',
                  sortBy: 'ASC',
                  skip: filterList?.length,
                  limit: 20,
                  search: filterSearch,
                  distinct: true,
                  getDBField: 'status'
                }
              }
            });
            break;
        }
      }
    }, 500);
    scrollDebounce();
  };

  const rowSelection = {
    fixed: 'left',
    columnWidth: 50,
    selectedRowKeys: selectedKeys,
    onChange: (selectedRowKeys) => {
      setSelectedKeys(selectedRowKeys);
    },
    renderCell(checked, record, index, node) {
      return React.cloneElement(node, {
        'aria-label': record?.id
      });
    }
  };

  const handleAddDynamicField = ({ key }) => {
    setSelectedDynamicKey(key);
    const otherFilters = { dynamicFieldFilter, paginationProp, filters };
    const stringifyObject = JSON?.stringify(otherFilters);

    history?.push(`${ROUTES?.DYNAMIC_FIELDS}/add`, {
      stringifyObject
    });
  };

  const handleEditDynamicField = (record) => {
    const otherFilters = { dynamicFieldFilter, paginationProp, filters };
    const stringifyObject = JSON?.stringify(otherFilters);
    setSelectedDynamicKey(record?.fieldType);
    history?.push(`${ROUTES?.DYNAMIC_FIELDS}/edit/${record?.id}`, {
      stringifyObject
    });
  };

  const handleResetFilter = (clearFilters, dataIndex) => {
    const filtersCopy = {
      ...filters,
      [dataIndex]: []
    };
    setFilters(filtersCopy);
    setFiltersCopyState(filtersCopy); // need to test here after clicking reset states are still
    dynamicFields({
      variables: {
        filter: {
          ...dynamicFieldFilter,
          skip: 0,
          sortOn: 'createdAt',
          sortBy: 'DESC'
        },
        where: {
          ...(filtersCopy && { ...filtersCopy }),
          entityType: 'PRODUCT'
        }
      }
    });
    setDynamicFieldsFilter({
      ...dynamicFieldFilter,
      skip: 0
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterIndex(dataIndex);
    setFilterVisible(false);
  };

  const importCallback = () => {
    setPaginationProp(initialPaginationValue);
    setDynamicFieldsFilter(initialDynamicFieldsFilter);
    setSortedInfo({});
    dynamicFields({
      variables: {
        filter: initialDynamicFieldsFilter,
        where: { entityType: 'PRODUCT' }
      }
    });
  };

  const changeFilter = (e, dataIndex) => {
    const {
      target: { value = '' }
    } = e;
    let filtersCopy = [];
    if (filtersCopyState?.[dataIndex]?.includes(value)) {
      filtersCopy = {
        ...filtersCopyState,
        [dataIndex]: filter(
          filtersCopyState?.[dataIndex],
          (item) => item !== value
        )
      };
    } else {
      filtersCopy = {
        ...filtersCopyState,
        [dataIndex]: filtersCopyState?.[dataIndex]
          ? [...filtersCopyState?.[dataIndex], value]
          : [value]
      };
    }
    setFiltersCopyState(filtersCopy);
  };

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination?.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setDynamicFieldsFilter({
        ...dynamicFieldFilter,
        skip,
        limit: pagination?.pageSize,
        sortOn: sorter?.field,
        sortBy: sorter?.order === 'ascend' ? 'ASC' : 'DESC'
      });
      dynamicFields({
        variables: {
          filter: {
            ...dynamicFieldFilter,
            skip,
            limit: pagination?.pageSize,
            sortOn: sorter?.field,
            sortBy: sorter?.order === 'ascend' ? 'ASC' : 'DESC'
          },
          where: {
            ...(filters && { ...filters }),
            entityType: 'PRODUCT'
          }
        }
      });
    } else {
      setDynamicFieldsFilter({
        ...dynamicFieldFilter,
        skip,
        limit: pagination?.pageSize,
        sortOn: 'createdAt',
        sortBy: 'DESC'
      });
      dynamicFields({
        variables: {
          filter: {
            ...dynamicFieldFilter,
            skip,
            limit: pagination?.pageSize,
            sortOn: 'createdAt',
            sortBy: 'DESC'
          },
          where: {
            ...(filters && { ...filters }),
            entityType: 'PRODUCT'
          }
        }
      });
    }
  };

  const handleSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setScrollFlag(false);
    switch (dataIndex) {
      case 'industry':
        industryFilters({
          variables: {
            filter: {
              sortOn: 'label',
              sortBy: 'ASC',
              skip: 0,
              limit: 20,
              search: value,
              getDBField: 'label'
            }
          }
        });
        break;

      case 'lineOfBusiness':
        lineOfBusinessFilters({
          variables: {
            filter: {
              sortOn: 'label',
              sortBy: 'ASC',
              skip: 0,
              limit: 20,
              search: value,
              justShow: true,
              getDBField: 'label'
            }
          }
        });
        break;
      case 'fieldType': {
        const filteredValue = filter(DYNAMIC_FIELDS_DATA, (item) => {
          if (
            (item?.label ?? '')?.toLowerCase()?.includes(value?.toLowerCase())
          ) {
            return item?.label;
          }
        });
        setFilterList(filteredValue);
        break;
      }
      default:
        dynamicFieldFilters({
          variables: {
            filter: {
              sortOn: 'status',
              sortBy: 'ASC',
              skip: 0,
              limit: 20,
              search: value,
              distinct: true,
              getDBField: 'status'
            }
          }
        });
        break;
    }
  };

  const handleDynamicFieldStatus = async (editDynamicFieldsData) => {
    const editData = {
      fieldType: editDynamicFieldsData?.fieldType,
      sortOrder: editDynamicFieldsData?.sortOrder,
      industryId: editDynamicFieldsData?.industryId,
      lineOfBusinessIds: editDynamicFieldsData?.lineOfBusinessIds,
      label: editDynamicFieldsData?.label,
      tooltip: editDynamicFieldsData?.tooltip,
      status:
        editDynamicFieldsData?.status === 'PUBLISH' ? 'INACTIVE' : 'PUBLISH',
      name: editDynamicFieldsData?.name,
      config: editDynamicFieldsData?.config
    };
    const response = await updateDynamicField({
      variables: {
        data: { ...editData },
        where: { id: editDynamicFieldsData?.id }
      }
    });
    if (response?.data?.updateDynamicField) {
      dynamicFields({
        variables: {
          filter: dynamicFieldFilter,
          where: {
            ...(filters && { ...filters }),
            entityType: 'PRODUCT'
          }
        }
      });
    }
  };

  const handleDeleteFilter = (value, dataIndex) => {
    const filtersCopy = {
      ...filtersCopyState,
      [dataIndex]: filter(
        filtersCopyState?.[dataIndex],
        (item) => item !== value
      )
    };
    setFiltersCopyState(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div className="custom-filter-dropdown">
        <LoaderComponent spinning={filterLoading} setHeight={35}>
          {dataIndex !== 'status' && (
            <SearchComponent
              className="list-search-box filter-search"
              id="search-container-id-roles"
              placeholder="Search..."
              name={dataIndex}
              getData={(value) => handleSearch(value, dataIndex)}
            />
          )}
          {filtersCopyState?.[dataIndex]?.length > 0 && (
            <div className="filter-section">
              {map(filtersCopyState?.[dataIndex], (item) => {
                return (
                  <Tag
                    key={item?.toString()}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    {dataIndex === 'status' ? (
                      <span title={statusRender(item)}>
                        {statusRender(item)}
                      </span>
                    ) : (
                      <>
                        {dataIndex === 'fieldType' ? (
                          <span
                            title={
                              find(DYNAMIC_FIELDS_DATA, ['key', item])?.label ||
                              item?.toString()
                            }
                          >
                            {find(DYNAMIC_FIELDS_DATA, ['key', item])?.label ||
                              item?.toString()}
                          </span>
                        ) : (
                          <span title={item?.label || item?.toString()}>
                            {item?.label || item?.toString()}
                          </span>
                        )}
                      </>
                    )}
                  </Tag>
                );
              })}
            </div>
          )}
          <div
            className="filter-checkboxes"
            onScroll={(e) => onScroll(e, dataIndex)}
          >
            {filterList?.length > 0 ? (
              map(filterList, (item) => {
                return (
                  <div
                    className="filter-checkbox-section"
                    key={item?.key || item?.id || item}
                  >
                    <Checkbox
                      value={item?.key || item?.id || item}
                      checked={filtersCopyState?.[dataIndex]?.includes(
                        item?.key || item?.id || item
                      )}
                      key={item?.key || item?.id || item}
                      onChange={(e) => changeFilter(e, dataIndex)}
                      className="common-checkbox"
                    >
                      {dataIndex === 'status' ? (
                        <span title={statusRender(item)}>
                          {statusRender(item)}
                        </span>
                      ) : (
                        <span title={item?.label || item?.toString()}>
                          {item?.label || item?.toString()}
                        </span>
                      )}
                    </Checkbox>
                  </div>
                );
              })
            ) : (
              <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        </LoaderComponent>
        <Divider className="divider-filter" />
        <div className="d-flex justify-center">
          <Button
            size="small"
            className="common-button discard-button filter-button"
            id="roles-filter-reset"
            onClick={() => handleResetFilter(clearFilters, dataIndex)}
          >
            Reset
          </Button>
          <Button
            size="small"
            className="common-button filter-button"
            id="roles-filter-ok"
            type="primary"
            onClick={() => getFilterData(confirm, dataIndex)}
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    filterIcon: () =>
      filters?.[dataIndex]?.length > 0 ? (
        <FilterSelectedIconComponent className="primary-color" />
      ) : (
        <img src={FilterIcon} alt="filter-icon" width={16} />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    }
  });

  const handleDeleteDynamicFields = async (editDynamicFieldsData) => {
    const response = await deleteDynamicFields({
      variables: { where: { id: editDynamicFieldsData?.id } }
    });
    if (response?.data) {
      dynamicFields({
        variables: {
          filter: dynamicFieldFilter,
          where: {
            ...(filters && { ...filters }),
            entityType: 'PRODUCT'
          }
        }
      });
    }
  };

  const renderActionButtons = (editDynamicFieldsData) => {
    return (
      <div className="d-flex flex-vertical">
        <AccessControl
          allowedPermissions={[
            'FET_DYNAMIC_FIELD_UPDATE',
            'FET_DYNAMIC_FIELD_VIEW'
          ]}
        >
          <Button
            id="dynamic-fields-table-edit-btn"
            className="b-0"
            onClick={() => {
              handleEditDynamicField(editDynamicFieldsData);
            }}
          >
            Edit
          </Button>
        </AccessControl>
        <AccessControl allowedPermissions={['FET_DYNAMIC_FIELD_DELETE']}>
          <Popconfirm
            title="Are you sure to delete?"
            onConfirm={() => handleDeleteDynamicFields(editDynamicFieldsData)}
            okText="Yes"
            cancelText="No"
          >
            <Button id="dynamic-fields-table-status-btn" className="b-0">
              Remove
            </Button>
          </Popconfirm>
        </AccessControl>
        {editDynamicFieldsData?.status !== 'DRAFT' && (
          <AccessControl allowedPermissions={['FET_DYNAMIC_FIELD_UPDATE']}>
            <Popconfirm
              title={`Are you sure to ${
                editDynamicFieldsData?.status === 'PUBLISH'
                  ? 'Mark Inactive'
                  : 'Mark Publish'
              }?`}
              onConfirm={() => handleDynamicFieldStatus(editDynamicFieldsData)}
              okText="Yes"
              cancelText="No"
            >
              <Button id="dynamic-fields-table-status-btn" className="b-0">
                {editDynamicFieldsData?.status === 'PUBLISH'
                  ? 'Mark Inactive'
                  : 'Mark Publish'}
              </Button>
            </Popconfirm>
          </AccessControl>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'LABEL',
      ellipsis: true,
      width: 130,
      dataIndex: 'label',
      key: 'label',
      sorter: true,
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'label' && sortedInfo?.order,
      render: (label) => <span>{label || '-'}</span>
    },
    {
      title: 'FIELD KEY',
      ellipsis: true,
      dataIndex: 'key',
      className: 'max-width-column',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'key' && sortedInfo?.order,
      key: 'key',
      render: (key) => <span>{key || '-'}</span>
    },
    {
      title: 'FIELD TYPE',
      width: 150,
      ellipsis: true,
      dataIndex: 'fieldType',
      key: 'fieldType',
      className: 'max-width-column',
      sorter: true,
      ...filterPopup('fieldType'),
      sortOrder: sortedInfo?.columnKey === 'fieldType' && sortedInfo?.order,
      render: (fieldType) => {
        return (
          <span>
            {find(DYNAMIC_FIELDS_DATA, ['key', fieldType])?.label || '-'}
          </span>
        );
      }
    },
    {
      title: 'INDUSTRIES',
      dataIndex: 'industryId',
      key: 'industryId',
      sorter: true,
      ellipsis: true,
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'industryId' && sortedInfo?.order,
      width: 150,
      ...filterPopup('industry'),
      render: (industryId, record = {}) => (
        <span>{record?.industry?.label || '-'}</span>
      )
    },
    {
      title: 'LINE OF BUSINESS',
      dataIndex: 'lineOfBusinessIds',
      key: 'lineOfBusinessIds',
      sorter: true,
      ellipsis: true,
      className: 'max-width-column',
      sortOrder:
        sortedInfo?.columnKey === 'lineOfBusinessIds' && sortedInfo?.order,
      width: 190,
      ...filterPopup('lineOfBusiness'),
      render: (lineOfBusinessIds, record = {}) => (
        <span>
          {record?.lineOfBusinesses?.length > 0
            ? record?.lineOfBusinesses?.map((item) => item?.label)?.join(', ')
            : '-'}
        </span>
      )
    },
    {
      title: 'ORDER',
      width: 100,
      ellipsis: true,
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'sortOrder' && sortedInfo?.order,
      render: (sortOrder) => <span>{sortOrder ?? '-'}</span>
    },
    {
      title: 'STATUS',
      width: 120,
      dataIndex: 'status',
      key: 'status',
      ...filterPopup('status'),
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'status' && sortedInfo?.order,
      render: (status) => {
        return statusRender(status);
      }
    },
    checkPermissions(permissions, [
      'FET_DYNAMIC_FIELD_UPDATE',
      'FET_DYNAMIC_FIELD_DELETE',
      'FET_DYNAMIC_FIELD_VIEW'
    ]) && {
      dataIndex: 'id',
      align: 'right',
      width: 10,
      fixed: 'right',
      render: (id, record) => {
        return (
          <Popover
            placement="bottom"
            overlayClassName="action-button"
            content={renderActionButtons(record)}
          >
            <MoreOutlined />
          </Popover>
        );
      }
    }
  ];

  const onSearchChange = (value) => {
    setDynamicFieldsFilter({
      ...dynamicFieldFilter,
      skip: value
        ? 0
        : dynamicFieldFilter?.limit * (paginationProp?.current - 1),
      search: trim(value)
    });
    setPaginationProp({ ...paginationProp, current: 1 });
    dynamicFields({
      variables: {
        filter: {
          ...dynamicFieldFilter,
          skip: value
            ? 0
            : dynamicFieldFilter?.limit * (paginationProp?.current - 1),
          search: trim(value)
        },
        where: {
          ...(filters && { ...filters }),
          entityType: 'PRODUCT'
        }
      }
    });
  };

  const handleExport = async (e) => {
    const { key } = e;
    setExportLoading(true);
    await handleExportCommon({
      filters,
      key,
      selectedKeys,
      module: 'DYNAMIC_FIELD'
    });
    setExportLoading(false);
  };

  return (
    <div>
      <ImportModal
        showImportModal={showImportModal}
        setShowImportModal={setShowImportModal}
        callback={importCallback}
        module="dynamic-field"
        folder="DYNAMIC_FIELD"
      />
      <AccessControl allowedPermissions={['FET_DYNAMIC_FIELD_CREATE']}>
        <Portal portalId="header-right-content">
          <Dropdown
            menu={{
              items: DYNAMIC_FIELDS_DATA,
              onClick: handleAddDynamicField
            }}
          >
            <Button
              className="common-button"
              icon={<PlusOutlined />}
              size="small"
              id="dynamic-fields-table-add-btn"
              type="primary"
            >
              Add Dynamic Field
            </Button>
          </Dropdown>
        </Portal>
      </AccessControl>

      <div className="mb-16 d-flex justify-between align-center">
        <div className="fill-width search-checkbox">
          <AccessControl allowedPermissions={['FET_DYNAMIC_FIELD_LIST']}>
            <SearchComponent
              className="list-search-box"
              id="search-container-id"
              placeholder="Dynamic Fields name or other detail.."
              name="DynamicFields"
              getData={onSearchChange}
              value={searchValue}
              setSearchValue={setSearchValue}
            />
          </AccessControl>
        </div>
        <div className="header-buttons">
          <AccessControl allowedPermissions={['FET_DYNAMIC_FIELD_IMPORT']}>
            <Button
              size="small"
              className="common-button import-button"
              icon={<img src={ImportIcon} alt="import-icon" width={11} />}
              id="dynamic-fields-table-import-btn"
              type="primary"
              onClick={() => handleShowImportModal(true)}
            >
              Import
            </Button>
          </AccessControl>
          <AccessControl allowedPermissions={['FET_DYNAMIC_FIELD_EXPORT']}>
            <Dropdown
              overlayClassName="export-btn-dropdown"
              menu={{ items: exportOptions, onClick: handleExport }}
              placement="bottom"
            >
              <Button
                className="common-button export-button"
                size="small"
                icon={<img src={ExportIcon} alt="export-icon" width={11} />}
                id="dynamic-fields-table-export-btn"
                loading={exportLoading}
                type="primary"
              >
                Export
              </Button>
            </Dropdown>
          </AccessControl>
        </div>
      </div>
      <AccessControl
        allowedPermissions={['FET_DYNAMIC_FIELD_LIST']}
        showNoAccess
      >
        <div className="common-table">
          {pageSize && (
            <TableComponent
              loadingData={
                loading ||
                updateDynamicFieldLoading ||
                deleteDynamicFieldsLoading
              }
              columns={[...columns?.filter((item) => item !== false)]}
              data={data?.dynamicFields?.data || []}
              onChange={handleTableChange}
              paginationConfig={paginationProp}
              rowSelection={rowSelection}
              rowKey={(obj) => obj?.id}
            />
          )}
        </div>
      </AccessControl>
    </div>
  );
};

export default DynamicFieldsTable;
